import { batchHeaderDate, renderBatchHeaderDetail } from '../../quotes/ui/batch-header-detail';
import { consume } from '@lit/context';
import { css, CSSResult, html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DataCacheGeneric } from '../../../webmodule-common/cache/generic-data-cache';
import { DataTableWrapper } from '../../../webmodule-common/other/ui/datatable-view';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { EventTemplate } from '../../../webmodule-common/other/ui/events';
import { flagInSet, isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { frameExpanderStyles } from '../../frames/frame-expander-styles';
import { FrameV2, FrameValidationType, Snippet } from '../../../webmodule-common/interop/webmodule-interop';
import { frameValidationIcon, getFrameDetailsTemplate } from '../../frames/ui';
import { getApiFactory } from '../../api/api-injector';
import {
  getV6FrameForContainer,
  getV6QuoteItemForContainer,
  isQuoteFrameVersionCurrent,
  isV6
} from '../../quotes/data/v6/helper-functions';
import { getV6QuoteItemSubtitleOrFrameDimensions } from '../../v6config/v6-ui';
import { orderItemCellStyles } from '../../../webmodule-common/other/ui/expandable-item-cell-styles';

import { ImagePreview, imagePreviewContext } from '../../../webmodule-common/other/context/imagePreview';
import {
  isFrame,
  isSpecialItem,
  isSupplierFrameItem,
  quoteItemThumbSrc
} from '../../quotes/data/quote-helper-functions';
import { LangPurchaseOrderDetailView } from '../../../localization/scripts/purchase-orders/purchase-order-detail-view-lang';
import { LitElementLangBase } from '../../../webmodule-common/other/litelement-base';
import { moneyToTemplateResult } from '../../../webmodule-common/other/currency-formatter';
import { monitor } from '../../../webmodule-common/other/monitor';
import { PurchaseOrderBatch, PurchaseOrderItem, PurchaseOrderState } from '../../api/dealer-api-interface-franchisee';
import { PurchaseOrderBatchDialog } from './purchase-order-batch-dialog';
import { PurchaseOrderContainerManager } from '../data/purchase-order-container';
import { QuoteItem } from '../../api/dealer-api-interface-quote';
import { QuoteItemContainer } from '../../quotes/data/quote-item-container';
import { RequestPage, ResultPaginated } from '../../../webmodule-common/other/ui/RequestPage';
import { ViewBase } from '../../../webmodule-common/other/ui/view-base';
import {
  WebmoduleIcon,
  WebModuleTable,
  WebModuleTableColumnDef,
  type WebModuleTableGroupActionTemplate,
  type WebModuleTableGroupHeaderTemplate
} from '../../../webmodule-common/components/src/webmodule-components';

interface PurchaseOrderItemTableOptions {
  purchaseOrderManager: PurchaseOrderContainerManager;
}

interface PurchaseOrderLineItem {
  purchaseOrderItem: PurchaseOrderItem;
  qic: QuoteItemContainer;
}

/**
 * Row-extension body for a purchase order line item: the frame option groups and
 * manufacturer notes. The host <webmodule-table> renders extension content inside
 * its shadow DOM where the page stylesheet cannot reach, so the component carries
 * its own theme-aware styles (same pattern as <franchisee-quote-item-expander>).
 */
@customElement('wm-purchase-order-item-expander')
export class PurchaseOrderItemExpander extends LitElementLangBase {
  static styles = frameExpanderStyles;

  @property({ attribute: false })
  public lineItem?: PurchaseOrderLineItem;

  protected render(): TemplateResult {
    const lineItem = this.lineItem;
    if (!lineItem) return html`${nothing}`;
    if (isSupplierFrameItem(lineItem.qic.item)) return this.getFrameExtendedDetails(lineItem);
    if (isSpecialItem(lineItem.qic.item)) return this.getSpecialItemExtendedDetails(lineItem);
    return html`${nothing}`;
  }

  protected getNotesTemplate(lineItem: PurchaseOrderLineItem): TemplateResult {
    if (!isEmptyOrSpace(lineItem.qic.item.commentToManufacturer)) {
      return html` <ul class="list-group">
        <li class="list-group-item list-group-item-primary">
          <span class="me-3 fw-bold">${LangPurchaseOrderDetailView.notesToManufacturer()}: </span>
          ${lineItem.qic.item.commentToManufacturer}
        </li>
      </ul>`;
    }
    return html``;
  }

  private getFrameExtendedDetails(lineItem: PurchaseOrderLineItem): TemplateResult {
    if (!isV6(lineItem.qic.item)) return html``;

    const data = getV6FrameForContainer(lineItem.qic);
    if (data) {
      return html` <div class="extended-options-line main-body">
        ${getFrameDetailsTemplate(data, true)} ${this.getNotesTemplate(lineItem)}
      </div>`;
    }
    return html``;
  }

  private getSpecialItemExtendedDetails(lineItem: PurchaseOrderLineItem): TemplateResult {
    if (!(isSpecialItem(lineItem.qic.item) && !isEmptyOrSpace(lineItem.qic.item.commentToManufacturer))) return html``;

    const warningTemplate = (): TemplateResult => {
      if (!isEmptyOrSpace(lineItem.qic.item.description)) return html``;

      return html`
        <ul class="list-group">
          <li class="list-group-item list-group-item-warning">
            <span class="me-3 fw-bold">${LangPurchaseOrderDetailView.reference()}</span>
            ${LangPurchaseOrderDetailView.pleaseProvideReference()}
          </li>
        </ul>
      `;
    };

    return html` <div class="extended-options-line main-body">
      ${this.getNotesTemplate(lineItem)} ${warningTemplate()}
    </div>`;
  }
}

/**
 * Styles injected into the <webmodule-table>'s shadow root via its `customStyles`
 * hook: cell-level layout the legacy light-DOM stylesheet used to provide.
 * (Row-extension content styles itself: <wm-purchase-order-item-expander>.)
 */
const poItemsTableStyles = css`
  ${orderItemCellStyles}

  /* Title cell: the title and description are separated by a <br>, so the pair always
     occupies two full lines - a touch looser than the shared default. */
  .wm-table-content .wm-table-cell.order-item-title {
    line-height: 1.2;
  }

  /* Item number: power-user bolt marker spacing. */
  .order-item-row .power-user {
    margin-left: 0.25rem;
  }

  /* Group-header content for delivery batches (rendered inside the table's native sticky header cell). */
  .po-item-group-title {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    white-space: nowrap;
  }
  .po-item-group-badge {
    font-weight: 400;
    background: var(--overwrite-main);
    color: var(--overwrite-light);
  }
  /* Inline batch "fast view" (delivery date / customer ref), badged so it carries the same weight as the
     item-count badge next to it rather than reading as incidental grey text. */
  .batch-header-detail {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-left: 0.5rem;
    white-space: nowrap;
  }
  .batch-header-badge {
    font-weight: 400;
    background: var(--overwrite-main);
    color: var(--overwrite-light);
  }
  /* Let the group-header cell take the free width so the action slot is pushed to the right edge. */
  .wm-table-group-header-cell {
    flex: 1 1 auto;
  }

  /* Batch action menu: sized to match the per-row action column (3rem) and centred so it sits directly
     under that column at the pinned right edge - same treatment as the quote items table. */
  .po-item-group-menu {
    width: 3rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .po-item-group-menu webmodule-icon-button {
    --webmodule-color-neutral-600: var(--overwrite-main);
  }
`;

@customElement('wm-purchaseorderitemtable')
export class PurchaseOrderItemTable extends DataTableWrapper<PurchaseOrderLineItem> {
  purchaseOrderManager: PurchaseOrderContainerManager;
  quoteApi = getApiFactory().quote();
  lineItems: PurchaseOrderLineItem[] = [];

  @consume({ context: imagePreviewContext })
  @property({ attribute: false })
  public imagePreview?: ImagePreview;

  @state()
  private _expandedOptions = false;

  constructor(options: PurchaseOrderItemTableOptions) {
    super();
    this.purchaseOrderManager = options.purchaseOrderManager;
  }

  /**
   * PO line item id → the number shown in the "No." column. THIS SCREEN AND THE PURCHASE-ORDER PDF MUST
   * AGREE ROW FOR ROW, so both apply the same three rules, in this order (see
   * GenerateReportsV2.PurchaseOrder.GetLineItems, which is the other half of this pair):
   *
   *  1. print the number RECORDED on the order when it was created. That is the number the dealer saw on
   *     the quote, counted over every item in the section: a batch of quote items 1, 2, 3 of which only 1
   *     and 3 were ordered prints 1 and 3. Charge lines are recorded and printed too - a charge is an item
   *     of its section, pushed to the end of it;
   *  2. failing that (an order raised before the snapshot existed), print the quote-wide presentation
   *     ordinal. Counting THIS order's own rows instead is what made a partial order read 1, 2, 3 here
   *     while the PDF read 3, 5, 7 for the same lines.
   *
   * Cached against the lineItems array IDENTITY rather than computed in getColumns: getColumns only re-runs
   * when the column *set* changes, so a captured map would go stale the moment the rows reloaded.
   */
  private _numberCacheSource: PurchaseOrderLineItem[] | null = null;
  private _numberCache = new Map<string, string>();

  private itemNumber(lineItem: PurchaseOrderLineItem): string {
    if (this._numberCacheSource !== this.lineItems) {
      this._numberCacheSource = this.lineItems;
      this._numberCache = new Map<string, string>();
      const ordinals = this.purchaseOrderManager.quotePresentationOrdinals();
      for (const row of this.lineItems) {
        const recorded = row.purchaseOrderItem.quoteItemBatchPosition;
        const number = recorded ?? ordinals.get(row.qic.item.commonId);
        if (number === undefined) continue;
        this._numberCache.set(row.purchaseOrderItem.id, String(number));
      }
    }
    return this._numberCache.get(lineItem.purchaseOrderItem.id) ?? '';
  }

  override getDefaultSortAsc(): boolean {
    return false;
  }

  // All items render on one page; the rows-per-page selector makes no sense here.
  // getRowsFromServer ignores the requested page and returns every line, so the
  // paginator could only mislead - exactly what disablePagination() is for.
  override disablePagination(): boolean {
    return true;
  }

  override getDefaultSortField(): string {
    return 'id';
  }

  // --- Delivery batches (MRD, Task 230006) -------------------------------------------------------
  // Group line items by the batch they were ORDERED under (the PO's own requested membership), so an
  // unbatched PO stays a flat table. The key is only a group when a matching PurchaseOrderBatch row
  // exists (a stale/orphan membership falls back to ungrouped rather than an empty header). Rows must
  // already be ordered so each batch's items are contiguous — see PurchaseOrderDetailView.loadLineItems.

  override getGroupByKey(): (item: PurchaseOrderLineItem) => string | null {
    return (item: PurchaseOrderLineItem): string | null => {
      const batchId = item.purchaseOrderItem.quoteItemBatchId;
      return batchId && this.purchaseOrderManager.batchFor(batchId) ? batchId : null;
    };
  }

  override groupHeaderTemplate(): WebModuleTableGroupHeaderTemplate {
    return (groupValue: unknown, groupItems: object[]): TemplateResult => {
      const batch = this.purchaseOrderManager.batchFor(String(groupValue));
      if (!batch) return html``;

      const name = isEmptyOrSpace(batch.description ?? undefined)
        ? LangPurchaseOrderDetailView.batchDetailsTitle()
        : batch.description!;
      const confirmedBadge = isEmptyOrSpace(batch.supplierSystemStatus ?? undefined)
        ? nothing
        : html`<span class="badge po-item-group-badge">${batch.supplierSystemStatus}</span>`;

      // Inline fast-view of delivery date / customer ref: prefer the supplier-confirmed (V6-synced) value,
      // falling back to the dealer-requested value; each shown only when set. Full detail is in the dialog.
      const batchDetail = renderBatchHeaderDetail([
        {
          label: LangPurchaseOrderDetailView.batchDeliveryDate(),
          value: batchHeaderDate(batch.supplierSystemDeliveryDate ?? batch.deliveryDate)
        },
        {
          label: LangPurchaseOrderDetailView.batchCustomerRef(),
          value: batch.supplierSystemCustomerRef ?? batch.customerRef
        }
      ]);

      return html`<span class="po-item-group-title">
        <webmodule-icon library="fa" name="fas-layer-group"></webmodule-icon>
        <span class="po-item-group-name">${name}</span>
        <span class="badge po-item-group-badge"
          >${LangPurchaseOrderDetailView.batchItemCount({ count: groupItems.length })}</span
        >
        ${confirmedBadge} ${batchDetail}
      </span>`;
    };
  }

  /**
   * The batch menu, rendered through the table's own group-action slot rather than inside the header
   * content. That slot is what pins it to the right edge, under the per-row action column - the same hook
   * the quote items table uses. Putting it in the header content instead leaves it trailing the text,
   * because the header cell is not the flex container that a `margin-left:auto` would need.
   */
  override groupActionTemplate(): WebModuleTableGroupActionTemplate {
    return (groupValue: unknown): TemplateResult => {
      const batch = this.purchaseOrderManager.batchFor(String(groupValue));
      if (!batch) return html``;
      return html`<span class="po-item-group-menu">${this.batchMenu(batch)}</span>`;
    };
  }

  // Group "..." menu — a single read-only "View Batch Details" action, mirroring the quote page's
  // read-only group menu. Opens the requested-vs-confirmed dialog.
  private batchMenu(batch: PurchaseOrderBatch): TemplateResult {
    const clickView = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      await PurchaseOrderBatchDialog.Execute(batch);
    };
    return html` <webmodule-dropdown class="item-menu text-start" placement="bottom-end" hoist>
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        <webmodule-menu-item @click=${clickView}>
          <webmodule-icon slot="prefix" library="fa" name="fas-eye"></webmodule-icon>
          ${LangPurchaseOrderDetailView.viewBatchDetails()}
        </webmodule-menu-item>
      </webmodule-menu>
    </webmodule-dropdown>`;
  }

  // The table renders cell content in its shadow DOM; carry the cell-layout
  // rules the page stylesheet used to provide.
  override customStyles(): CSSResult {
    return poItemsTableStyles;
  }

  override async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<PurchaseOrderLineItem>> {
    return {
      count: this.lineItems.length,
      pageCount: 1,
      pageIndex: 0,
      pageSize: this.lineItems.length,
      results: this.lineItems
    };
  }

  override getColumns(): WebModuleTableColumnDef[] {
    const cols: WebModuleTableColumnDef[] = [];

    const addColumnDef = (columnId: string, column: WebModuleTableColumnDef) => {
      cols.push({ ...column, id: columnId, sortable: false });
    };

    addColumnDef('number', {
      title: () => LangPurchaseOrderDetailView.itemNumberTitle(),
      fieldName: 'id',
      classes: 'order-item-row',
      minWidth: '60px',
      maxWidth: '80px',
      displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
        const lineItem = item as PurchaseOrderLineItem;
        // Per-section number, not the row index: a batched PO restarts numbering inside each batch, the
        // same as the quote. Charge lines get no number at all.
        const num = this.itemNumber(lineItem);
        return lineItem.qic.item.isRestrictedToPowerUser
          ? html`${num} <webmodule-icon class="power-user" library="fa" name="fas-bolt"></webmodule-icon>`
          : html`${num}`;
      }
    });

    addColumnDef('thumbnail', {
      title: () => LangPurchaseOrderDetailView.imageTitle(),
      fieldName: 'id',
      classes: 'order-item-image',
      width: '80px',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const lineItem = item as PurchaseOrderLineItem;
        const quoteItem = lineItem.qic.item;

        // Deliberately untagged: PO rows are not backed by a loaded quote-item container, so the
        // asset's content hash is not available here (see assetContentTag). These images are read-only
        // and are not edited from this view, so revalidation on page load is sufficient.
        const src =
          quoteItem.virtualThumbnailPath === ''
            ? quoteItemThumbSrc(quoteItem)
            : this.quoteApi.convertQuoteItemAssetPathToUrl(
                quoteItem.quoteId,
                quoteItem.id,
                quoteItem.virtualThumbnailPath
              );

        const imagePreview = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();

          if (isFrame(quoteItem) || !isEmptyOrSpace(quoteItem.virtualThumbnailPath))
            this.imagePreview?.showImagePreview(
              this.quoteApi.convertQuoteItemAssetPathToUrl(
                quoteItem.quoteId,
                quoteItem.id,
                quoteItem.virtualThumbnailPath
              ),
              'quote-item-thumb'
            );
        };

        const hidePreview = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          this.imagePreview?.hideImagePreview();
        };

        return html` <div
          @mouseenter=${imagePreview}
          @mouseleave=${hidePreview}
          @touchstart=${imagePreview}
          @touchend=${hidePreview}
          @touchcancel=${hidePreview}
        >
          <img
            class="quote-item-thumbnail"
            alt="Thumbnail for quote item"
            src="${src}"
            style="width:48px; height:48px"
          />
        </div>`;
      }
    });

    const purchaseOrderItemTitleDisplay = (item: QuoteItem) => {
      let title = item.title;
      if (isEmptyOrSpace(title)) {
        title = LangPurchaseOrderDetailView.frameTitle();
      }
      return html`<span class="quote-item-title">${title}</span><br />
        <span class="quote-item-description">${item.description}</span>`;
    };

    addColumnDef('title', {
      title: () => LangPurchaseOrderDetailView.frameTitleAndDescription(),
      fieldName: 'title',
      classes: 'order-item-title',
      minWidth: '200px',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const lineItem = item as PurchaseOrderLineItem;
        return purchaseOrderItemTitleDisplay(lineItem.qic.item);
      },
      // Grand-total footer row label; the amount renders in the price column.
      columnAggregate: () => LangPurchaseOrderDetailView.orderTotal(),
      // Per-batch footer label. Without a closing row on each batch there is no visual end to a group, so
      // the ungrouped charge lines that follow the last batch look like members of it.
      groupAggregate: () => html`<strong>${LangPurchaseOrderDetailView.batchTotal()}</strong>`
    });

    const expandButton = html`
      <span>${LangPurchaseOrderDetailView.optionsTitle()}</span>
      <webmodule-icon
        id="quote-item-expand-all-indicator"
        library="fa"
        name="fas-angles-down"
        @click=${() => (this._expandedOptions = !this._expandedOptions)}
      ></webmodule-icon>
    `;

    addColumnDef('options', {
      title: html`${expandButton}`,
      fieldName: 'description',
      classes: 'order-item-description',
      minWidth: '200px',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const lineItem = item as PurchaseOrderLineItem;
        const quoteItem = lineItem.qic.item;
        try {
          if (isFrame(quoteItem) || isSpecialItem(quoteItem))
            return this.generateQuoteItemOptionsView(lineItem, _table.isExpanded(lineItem) != undefined);
          else if (quoteItem.providerReferenceId === emptyGuid) {
            return quoteItem.description;
          } else return '';
        } catch {
          return '';
        }
      },
      click: (e: Event, table: WebModuleTable, item: unknown) => {
        e.stopPropagation();
        e.preventDefault();

        const rowItem = item as PurchaseOrderLineItem;
        const isShown = table.isExpanded(rowItem);

        if (isShown) table.remExtension(rowItem);
        else {
          table.addExtension(rowItem, this.getExtendedDetails(rowItem));
        }

        return true;
      }
    });

    const tooltipIcons = html` <webmodule-tooltip placement="right" class="table-header-tooltip" hoist>
      <div slot="content">
        <div>
          <webmodule-icon library="fa" name="fas-message" color="black"></webmodule-icon>
          <span>${LangPurchaseOrderDetailView.notesToManufacturer()}</span>
        </div>
        <small>${LangPurchaseOrderDetailView.frameConfiguration()}</small>
        <webmodule-divider></webmodule-divider>
        <div>
          <webmodule-icon library="fa" name="fas-circle-info" color="cyan"></webmodule-icon>
          <span>${LangPurchaseOrderDetailView.information()}</span>
        </div>
        <div>
          <webmodule-icon library="fa" name="fas-file-lines" color="primary"></webmodule-icon>
          <span>${LangPurchaseOrderDetailView.supplierNote()}</span>
        </div>
        <div>
          <webmodule-icon library="fa" name="fas-triangle-exclamation" color="yellow"></webmodule-icon>
          <span>${LangPurchaseOrderDetailView.warning()}</span>
        </div>
        <div>
          <webmodule-icon library="fa" name="fas-circle-exclamation" color="red"></webmodule-icon>
          <span>${LangPurchaseOrderDetailView.criticalError()}</span>
        </div>
        <div>
          <webmodule-icon library="fa" name="fas-wrench" color="red"></webmodule-icon>
          <span>${LangPurchaseOrderDetailView.requireValidation()}</span>
        </div>
      </div>

      <span
        >${LangPurchaseOrderDetailView.tags()} <webmodule-icon library="fa" name="far-circle-question"></webmodule-icon
      ></span>
    </webmodule-tooltip>`;

    addColumnDef('tags', {
      title: tooltipIcons,
      classes: 'order-item-tags',
      fieldName: 'id',
      minWidth: '50px',
      maxWidth: '100px',
      displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
        const lineItem = item as PurchaseOrderLineItem;

        try {
          return this.getitemTags(lineItem);
        } catch {
          return html``;
        }
      }
    });

    addColumnDef('quantity', {
      title: () => LangPurchaseOrderDetailView.quantityTitle(),
      fieldName: 'quantity',
      classes: 'order-item-quantity',
      minWidth: '50px',
      maxWidth: '100px',
      align: 'right',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const lineItem = item as PurchaseOrderLineItem;
        return html`${lineItem.qic.item.quantity}`;
      }
    });

    addColumnDef('price', {
      title: () => LangPurchaseOrderDetailView.priceTitle(),
      fieldName: 'id',
      classes: 'order-item-price',
      minWidth: '90px',
      maxWidth: '120px',
      align: 'right',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const lineItem = item as PurchaseOrderLineItem;
        const supplierAdjustment = lineItem.qic.price.supplierPriceAdjustmentEnabled
          ? lineItem.qic.price.supplierPriceAdjustment
          : 0;
        const price = lineItem.qic.price.supplierNettQuantityCost + supplierAdjustment;
        return moneyToTemplateResult(price, this.purchaseOrderManager.purchaseOrderCurrency);
      },
      // The authoritative order total (net + adjustments), matching the pricing
      // summary drawer, rather than a client-side sum of the visible rows.
      columnAggregate: () => {
        const purchaseOrder = this.purchaseOrderManager.purchaseOrder;
        return moneyToTemplateResult(
          purchaseOrder.calculatedNetTotal + purchaseOrder.calculatedAdjustmentTotal,
          this.purchaseOrderManager.purchaseOrderCurrency
        );
      },
      // Per-batch total. Summed from the batch's own rows (the same net + adjustment each row displays) -
      // unlike the grand total there is no server-side per-batch figure to defer to.
      groupAggregate: (groupItems: object[]) =>
        moneyToTemplateResult(
          (groupItems as PurchaseOrderLineItem[]).reduce((total, lineItem) => {
            const supplierAdjustment = lineItem.qic.price.supplierPriceAdjustmentEnabled
              ? lineItem.qic.price.supplierPriceAdjustment
              : 0;
            return total + lineItem.qic.price.supplierNettQuantityCost + supplierAdjustment;
          }, 0),
          this.purchaseOrderManager.purchaseOrderCurrency
        )
    });

    return cols;
  }

  getitemTags(lineItem: PurchaseOrderLineItem) {
    const validationIndicator = () => {
      const icon = frameValidationIcon(this.getItemValidationType(lineItem));
      return icon ? html` ${icon}` : html``;
    };
    const notesManufacturerIndicator = () => {
      if (lineItem.qic.item.commentToManufacturer)
        return html` <webmodule-icon library="fa" name="fas-message" color="black"></webmodule-icon>`;
      else return html``;
    };

    return html`${notesManufacturerIndicator()}${validationIndicator()}`;
  }

  public isOutOfDate(): boolean {
    for (let i = 0; i < this.lineItems.length; i++) {
      const item = this.lineItems[i];
      if (isFrame(item.qic.item) || isSpecialItem(item.qic.item)) {
        const state = this.getItemValidationType(item);
        if (state === FrameValidationType.outOfDate) return true;
      }
    }
    return false;
  }

  generateQuoteItemOptionsView(lineItem: PurchaseOrderLineItem, childVisible: boolean): TemplateResult {
    if (isSpecialItem(lineItem.qic.item) && isEmptyOrSpace(lineItem.qic.item.comment))
      return html`<span>${this.getOptionsOneLiner(lineItem)}</span>`;

    // webmodule-icon rather than a raw <i>: Font Awesome's class rules do not
    // reach the table's shadow DOM.
    if (childVisible)
      return html`<span>${this.getOptionsOneLiner(lineItem)}</span
        ><span class="options-dropdown"><webmodule-icon library="fa" name="fas-caret-up"></webmodule-icon></span>`;
    else
      return html`<span>${this.getOptionsOneLiner(lineItem)}</span
        ><span class="options-dropdown"><webmodule-icon library="fa" name="fas-caret-down"></webmodule-icon></span>`;
  }

  getItemValidationType(lineItem: PurchaseOrderLineItem): FrameValidationType {
    //we are currently only supporting items that are for v6
    if (!isV6(lineItem.qic.item)) return FrameValidationType.nothing;

    const data = this.getFrameData(lineItem);
    if (!data) return FrameValidationType.nothing;
    const quoteItemContainer: QuoteItemContainer = lineItem.qic;

    if (
      flagInSet(this.purchaseOrderManager.purchaseOrder.state, PurchaseOrderState.Draft) &&
      !isQuoteFrameVersionCurrent(quoteItemContainer)
    )
      return FrameValidationType.outOfDate;
    return FrameValidationType.nothing;
  }

  getOptionsOneLiner(lineItem: PurchaseOrderLineItem) {
    //only proceed if this is not a frame else return the description
    if (!isV6(lineItem.qic.item)) return lineItem.qic.item.description;

    const data = getV6QuoteItemForContainer(lineItem.qic);
    return getV6QuoteItemSubtitleOrFrameDimensions(data);
  }

  getExtendedDetails(lineItem: PurchaseOrderLineItem): TemplateResult {
    // The extension renders in the table's shadow DOM; the expander component
    // carries its own styles there.
    return html`<wm-purchase-order-item-expander .lineItem=${lineItem}></wm-purchase-order-item-expander>`;
  }

  private getFrameData(lineItem: PurchaseOrderLineItem): FrameV2 | undefined {
    if (!isV6(lineItem.qic.item)) return undefined;
    return getV6FrameForContainer(lineItem.qic);
  }

  @monitor('_expandedOptions', { delayUntilFirstUpdate: true })
  public async handleExpandCollapse() {
    if (!this.dataTable) return;

    this.lineItems.forEach(x => {
      const rowItem = x;

      const isShown = this.dataTable!.isExpanded(rowItem);

      if (this._expandedOptions && !isShown) this.dataTable!.addExtension(rowItem, this.getExtendedDetails(rowItem));
      else if (!this._expandedOptions && isShown) this.dataTable!.remExtension(rowItem);
    });

    // The header (and its indicator icon) renders inside the table's shadow root.
    const indicator = this.dataTable.shadowRoot?.querySelector<WebmoduleIcon>('#quote-item-expand-all-indicator');
    if (!indicator) return;

    if (this._expandedOptions) indicator.name = 'fas-angles-up';
    else indicator.name = 'fas-angles-down';
  }
}

export interface PurchaseOrderDetailViewOptions {
  purchaseOrderManager: PurchaseOrderContainerManager;
  purchaseOrderCache: DataCacheGeneric;
}

@customElement('wm-purchaseorderdetailview')
export class PurchaseOrderDetailView extends ViewBase {
  protected readonly purchaseOrderManager: PurchaseOrderContainerManager;
  protected table: PurchaseOrderItemTable;
  protected purchaseOrderCache: DataCacheGeneric;
  lineItems: PurchaseOrderLineItem[] = [];

  constructor(options: PurchaseOrderDetailViewOptions) {
    super();
    this.purchaseOrderManager = options.purchaseOrderManager;
    this.purchaseOrderCache = options.purchaseOrderCache;
    this.table = this.ItemTableFactory();
  }

  public isOutOfDate(): boolean {
    return this.table.isOutOfDate();
  }

  public async prepareForSave() {
    throw new DevelopmentError('items-view, Method not implemented.');
  }

  async afterConstruction(): Promise<void> {
    await this.loadLineItems();
  }

  public async loadLineItems() {
    await this.purchaseOrderManager.needsPurchaseOrder();

    const items = this.purchaseOrderManager.items;
    const containers = this.purchaseOrderManager.quoteItemContainers;

    const mapped = items
      .map(pi => {
        const pli: PurchaseOrderLineItem = {
          purchaseOrderItem: pi,
          qic: containers.find(c => c.item.id === pi.quoteItemId)!
        };
        return pli;
      })
      .filter(li => li.qic !== undefined);

    this.lineItems = this.orderLineItemsByBatch(mapped);
    this.table.lineItems = this.lineItems;
  }

  /**
   * Order rows so items in the same delivery batch are contiguous (native grouping requires it): the
   * batches first in the PO's batch order, then the ungrouped (loose) items, each set keeping its existing
   * order. An unbatched PO is returned unchanged (flat display).
   *
   * Batches before loose items mirrors the quote, where the forced supplier lines (quote-level shipping,
   * warranties, price adjustments) are pushed to the bottom — a PO that listed them first would read in a
   * different order from the quote it came from, for the same data.
   */
  private orderLineItemsByBatch(items: PurchaseOrderLineItem[]): PurchaseOrderLineItem[] {
    const batches = this.purchaseOrderManager.batches;
    if (batches.length === 0) return items;

    // Rank on the recorded SortOrder, not on the position the batch happens to occupy in the array. The
    // read orders by it too, but sorting here as well means this screen and the report cannot disagree
    // about batch order even if a caller hands the collection over unsorted.
    const batchOrder = new Map<string, number>();
    [...batches]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .forEach((b, i) => batchOrder.set(b.quoteItemBatchId, i));

    const rankOf = (li: PurchaseOrderLineItem): number => {
      const batchId = li.purchaseOrderItem.quoteItemBatchId;
      // Loose items (and stale memberships with no batch row) sort after every batch.
      return batchId && batchOrder.has(batchId) ? batchOrder.get(batchId)! : batches.length;
    };

    // Array.sort is stable (ES2019+), so items keep their original order within each batch / the loose set.
    return [...items].sort((a, b) => rankOf(a) - rankOf(b));
  }

  public getValidationErrors(): string[] {
    return [];
  }

  public async refreshData(): Promise<void> {
    //this is a force reload of the data. This is not something that we want to do, if items are inuse
    //as we would end up with a bad loading of data.
    await this.loadLineItems();
    await this.table.refreshData();
    this.requestUpdate();
  }

  public async invalidate(): Promise<void> {
    this.requestUpdate();
    this.table.refreshData();
  }

  public buttonMenu(): Snippet {
    return html``;
  }

  protected ItemTableFactory(): PurchaseOrderItemTable {
    return new PurchaseOrderItemTable({
      purchaseOrderManager: this.purchaseOrderManager
    });
  }

  protected template(): EventTemplate {
    return html` <div>${this.table}</div>`;
  }
}
